import React from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';
import {
  pdfColors, pdfFontSize, pdfLetter, pdfRadius, pdfSpacing, pdfStatusTones, type PdfTone,
} from '../theme';

export interface StatItem {
  label: string;
  value: string | number;
  /** Small line under the value ("+10% overbook", "86% of batch"). */
  caption?: string;
  /** Colours the value and a short accent bar above the label. */
  tone?: PdfTone;
  /** Share of the strip's width (default 1). Fonts never break inside a
   *  word, so a long unbroken value (a roll number) needs a wider cell. */
  weight?: number;
}

interface Props {
  items: StatItem[];
  /** Smaller values — for a record header rather than a report summary. */
  dense?: boolean;
}

const styles = StyleSheet.create({
  strip: {
    flexDirection: 'row',
    borderWidth: 0.6,
    borderColor: pdfColors.divider,
    borderStyle: 'solid',
    borderRadius: pdfRadius.lg,
    marginBottom: pdfSpacing.xl,
  },
  cell: { paddingHorizontal: 14, paddingVertical: 10 },
  divider: { borderLeftWidth: 0.6, borderLeftColor: pdfColors.divider, borderLeftStyle: 'solid' },
  accent: { width: 16, height: 2.5, borderRadius: 1, marginBottom: 6 },
  label: {
    fontSize: pdfFontSize.xxs,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: pdfLetter.openLabel,
    color: pdfColors.textMuted,
  },
  value: {
    fontSize: pdfFontSize.display,
    fontWeight: 700,
    letterSpacing: pdfLetter.tight,
    color: pdfColors.textPrimary,
    marginTop: 2,
  },
  valueDense: { fontSize: pdfFontSize.lg, letterSpacing: 0 },
  caption: { fontSize: pdfFontSize.xxs, color: pdfColors.textMuted, marginTop: 1 },
});

/** Row of headline numbers in one bordered strip — the at-a-glance summary
 *  above a report (seat snapshot, batch tallies). Never splits. */
const StatStrip: React.FC<Props> = ({ items, dense }) => (
  <View style={styles.strip} wrap={false}>
    {items.map((it, i) => {
      const tone = it.tone ? pdfStatusTones[it.tone] : null;
      return (
        <View key={it.label} style={[styles.cell, i === 0 ? {} : styles.divider, { flex: it.weight ?? 1 }]}>
          {/* Reserved on every cell so labels align; inked only when toned. */}
          <View style={[styles.accent, { backgroundColor: tone ? tone.fg : 'transparent' }]} />
          <Text style={styles.label}>{it.label}</Text>
          <Text style={[styles.value, dense ? styles.valueDense : {}, tone ? { color: tone.fg } : {}]}>
            {it.value}
          </Text>
          {it.caption && <Text style={styles.caption}>{it.caption}</Text>}
        </View>
      );
    })}
  </View>
);

export default StatStrip;
